<template>
  <div id="event-analytics-detail" class="space-y-4 px-4 py-3">
    <!-- Top: event header + timeline (the list item, moved here) -->
    <section class="rounded-sm bg-[#232b33] p-4 roundedn-sm">
      <div class="mb-3 flex flex-wrap items-center justify-between gap-2">
        <button
          class="inline-flex h-7 items-center gap-1 px-2 text-xs font-bold tracking-tight rounded cursor-pointer transition-colors bg-black/60 text-white! hover:opacity-80"
          @click="goBack"
        >
          <i class="el-icon-arrow-left"></i>
          Back to events
        </button>

        <div class="flex shrink-0 flex-wrap items-center gap-1">
          <button
            class="h-6 px-3 text-xs font-bold tracking-tight border rounded cursor-pointer transition-colors border-black!"
            :class="showInfo ? 'bg-sky-500/25 text-sky-200!' : 'bg-black/60 text-white/50!'"
            :title="showInfo ? 'Hide info-level anomalies' : 'Show info-level anomalies'"
            @click="toggleShowInfo"
          >
            <i class="bi bi-info-circle mr-1"></i>
            Info{{ infoAnomalyCount ? ` (${infoAnomalyCount})` : '' }}
          </button>
          <button
            v-if="hasReport"
            class="h-6 px-3 text-xs font-bold tracking-tight border rounded cursor-pointer transition-colors bg-black/60 border-black! text-white! hover:opacity-80"
            :disabled="analyzingIncidents"
            @click="analyzeAll"
          >
            <i v-if="analyzingIncidents" class="el-icon-loading mr-1"></i>
            Analyze all
          </button>
          <button
            class="h-6 px-3 text-xs font-bold tracking-tight border rounded cursor-pointer transition-colors bg-black/60 border-black! text-white! hover:opacity-80"
            :disabled="loadingReport && processingAction === 'report'"
            @click="refreshReport"
          >
            <i v-if="loadingReport && processingAction === 'report'" class="el-icon-loading mr-1"></i>
            Refresh
          </button>
          <button
            class="h-6 px-3 text-xs font-bold tracking-tight rounded cursor-pointer transition-colors bg-yellow-600 text-black! hover:opacity-80"
            :disabled="generatingReport"
            @click="generateReport"
          >
            <i v-if="generatingReport" class="el-icon-loading mr-1"></i>
            Generate
          </button>
        </div>
      </div>

      <div v-if="!event" class="rounded-sm bg-black/30 p-4 text-sm text-white/55">
        <i v-if="loadingEvents" class="el-icon-loading mr-1"></i>
        {{ loadingEvents ? 'Loading event…' : 'Event not found in the selected range.' }}
      </div>

      <template v-else>
        <div class="flex min-w-0 flex-wrap items-center gap-2">
          <span class="max-w-[70%] truncate text-sm font-semibold text-white/95">{{ eventTitle }}</span>
          <span
            class="rounded-full px-2 py-1 text-[10px] font-bold uppercase leading-none tracking-[0.02em]"
            :class="eventStatusTagClass(event.status)"
          >
            {{ event.status }}
          </span>
          <span
            v-if="hasReport"
            class="inline-flex items-center gap-1 rounded-full bg-black/40 px-2 py-1 text-[10px] uppercase leading-none tracking-[0.02em] text-white/60"
          >
            <i class="el-icon-document"></i>report
          </span>
          <span
            v-if="processingAction"
            class="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.03em] text-white/75"
          >
            <i class="el-icon-loading"></i>
            {{ processingActionLabel }}
          </span>
          <bly-signal-group-legend class="ml-auto" />
        </div>

        <div class="mt-1 text-xs text-white/60">
          {{ eventDateRangeText }} * {{ customerName }} / {{ accountName }}
        </div>

        <div class="mt-3">
          <bly-event-analytics-resource-timeline
            :event="event"
            :groups="visibleGroups"
            :selected-incident-id="selectedIncidentId"
            :selected-anomaly-id="selectedAnomalyId"
            @select-incident="handleIncidentSelect"
          />
        </div>
      </template>
    </section>

    <!-- Top (cont.): exec summary -->
    <bly-event-analytics-summary-card
      :event="event"
      :summary="normalized.summary"
      :ai-event-summary="normalized.aiEventSummary"
      :metric-totals="normalized.metricTotals"
      :resource-metrics="normalized.resourceMetrics"
      :thresholds="normalized.thresholds"
      :loading="reportLoading"
    />

    <!-- Bottom: incident drilldown (details panel + sub-timeline) -->
    <div v-if="reportLoading" class="space-y-2 rounded-sm bg-[#232b33] p-4">
      <div class="flex items-center gap-2">
        <i class="el-icon-loading text-white/70"></i>
        <span class="text-xs text-white/55">Loading incident data…</span>
      </div>
      <div class="h-3 w-2/3 animate-pulse rounded bg-white/10"></div>
      <div class="h-8 w-full animate-pulse rounded bg-white/10"></div>
      <div class="h-16 w-full animate-pulse rounded bg-white/10"></div>
    </div>

    <template v-else>
      <bly-event-analytics-incident-timeline
        v-if="selectedGroup"
        :group="selectedGroup"
        :event="event"
        :fallback-range="filterRangeMs"
        :analyzing="analyzingIncidents"
        :initial-anomaly-id="selectedAnomalyId"
        @analyze-incidents="handleAnalyzeIncidents"
        @close="clearSelectedGroup"
      />
      <div v-else class="rounded-sm bg-[#232b33] p-4 text-sm text-white/60">
        Select a warning/error block in the timeline above to see incident details.
      </div>
    </template>
  </div>
</template>

<script setup>
import {
  ref, computed, watch, onMounted, getCurrentInstance,
} from 'vue';
import moment from 'moment';
// `Bly` prefix keeps the kebab-case template tags resolving under script-setup
// automatic component registration.
import BlyEventAnalyticsResourceTimeline from '../components/EventAnalyticsResourceTimeline.vue';
import BlyEventAnalyticsSummaryCard from '../components/EventAnalyticsSummaryCard.vue';
import BlyEventAnalyticsIncidentTimeline from '../components/EventAnalyticsIncidentTimeline.vue';
import BlySignalGroupLegend from '../components/SignalGroupLegend.vue';

const { proxy } = getCurrentInstance();
const store = proxy.$store;

const FILTERS_STORAGE_KEY = 'eventAnalytics.filters';
// Own key: the list view rewrites the whole `eventAnalytics.filters` payload,
// which would drop anything the detail view stored in it.
const SHOW_INFO_STORAGE_KEY = 'eventAnalytics.showInfo';

const defaultAiConfig = () => ({
  aiSummary: false,
  incidentsAiAnalysis: false,
  aiProvider: 'openai',
  aiModel: 'gpt-5.4-mini',
  aiMaxTokens: 1000,
});

const emptyNormalized = () => ({
  summary: {
    status: null,
    incidentCount: 0,
    criticalCount: 0,
    errorCount: 0,
    warningCount: 0,
    infoCount: 0,
    durationSeconds: 0,
    highlights: [],
    totals: {},
  },
  aiEventSummary: null,
  incidentGroups: [],
  anomalies: [],
  dataRange: { from: null, to: null },
  metricTotals: [],
  resourceMetrics: [],
  thresholds: {},
});

// Reads the persisted filter range + AI config from localStorage (shared with
// the list view). Defined first so state below can initialise from it.
function readStored() {
  const fallback = {
    range: {
      startDate: new Date(new Date().setDate(new Date().getDate() - 7)),
      endDate: new Date(),
    },
    aiConfig: defaultAiConfig(),
  };
  try {
    const raw = window.localStorage.getItem(FILTERS_STORAGE_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return {
      range: {
        startDate: parsed.startDate ? new Date(parsed.startDate) : fallback.range.startDate,
        endDate: parsed.endDate ? new Date(parsed.endDate) : fallback.range.endDate,
      },
      aiConfig: { ...defaultAiConfig(), ...(parsed.aiConfig || {}) },
    };
  } catch (error) {
    return fallback;
  }
}

const stored = readStored();

// Reactive state (was data()).
const eventId = ref(proxy.$route.params.eventId);
// Specific report to display (?reportId=). Defaults to the event's newest
// report when absent. Lets us deep-link / compare a specific report.
const reportId = ref(proxy.$route.query.reportId || null);
// Persisted filter range (start/end) used as the Grafana/link fallback when
// the event has no explicit time window.
const storedRange = stored.range;
const aiConfig = stored.aiConfig;
// Show/hide info-level anomalies (persisted). Defaults to shown.
const showInfo = ref(window.localStorage.getItem(SHOW_INFO_STORAGE_KEY) !== '0');
const selectedIncidentId = ref(proxy.$route.query.incidentId || null);
// Specific anomaly the user clicked (opens straight to its details).
const selectedAnomalyId = ref(proxy.$route.query.anomalyId || null);
// Inbound deep-link hints (ops monitoring / notifications): focus the incident
// affecting this resource. Matched by id or name, then dropped from the URL.
const selectedResourceId = ref(proxy.$route.query.resourceId || null);
const selectedResourceName = ref(proxy.$route.query.resourceName || null);
// Stable fallback key so the open incident survives a re-normalize whose
// group ids may shift.
const selectedGroupStartMs = ref(null);
const processingAction = ref(null);
// True only while we actually go to the network for this event's report.
const reportLoadingActive = ref(false);

// Store state (was mapState).
const loadingEvents = computed(() => store.state.eventAnalytics.loadingEvents);
const loadingReport = computed(() => store.state.eventAnalytics.loadingReport);
const generatingReport = computed(() => store.state.eventAnalytics.generatingReport);
const analyzingIncidents = computed(() => store.state.eventAnalytics.analyzingIncidents);
const events = computed(() => store.state.eventAnalytics.events);

// Store getters (each returns a function — was mapGetters).
const getEventReport = (id) => store.getters['eventAnalytics/getEventReport'](id);
const getEventNormalized = (id) => store.getters['eventAnalytics/getEventNormalized'](id);
const hasEventReport = (id) => store.getters['eventAnalytics/hasEventReport'](id);

const event = computed(() => (events.value || []).find((item) => item && item.id === eventId.value) || null);
const normalized = computed(() => getEventNormalized(eventId.value) || emptyNormalized());
const incidentGroups = computed(() => normalized.value.incidentGroups || []);
// Info-level anomalies are usually the noisiest ones; the Info toggle drops them
// (and any incident left empty) from the timelines and the drilldown.
const visibleGroups = computed(() => {
  if (showInfo.value) return incidentGroups.value;
  return incidentGroups.value
    .map((group) => ({ ...group, anomalies: (group.anomalies || []).filter((a) => a.severity !== 'info') }))
    .filter((group) => group.anomalies.length);
});
const infoAnomalyCount = computed(() => incidentGroups.value
  .reduce((acc, group) => acc + (group.anomalies || []).filter((a) => a.severity === 'info').length, 0));
const hasReport = computed(() => hasEventReport(eventId.value));
const reportLoading = computed(() => reportLoadingActive.value);
const selectedGroup = computed(() => {
  if (!selectedIncidentId.value && selectedGroupStartMs.value === null) return null;
  const groups = visibleGroups.value;
  return groups.find((group) => group.id === selectedIncidentId.value)
    || groups.find((group) => group.startMs === selectedGroupStartMs.value)
    || null;
});
const eventTitle = computed(() => (event.value ? (event.value.fixture || event.value.name || event.value.id) : '—'));
const customerName = computed(() => (event.value && event.value.customer ? event.value.customer.name : '-'));
const accountName = computed(() => (event.value && event.value.account ? event.value.account.name : '-'));
const eventDateRangeText = computed(() => {
  const lineup = event.value && event.value.lineupTime ? moment.utc(event.value.lineupTime).format('DD/MM HH:mm') : '--';
  const end = event.value && event.value.endTime ? moment.utc(event.value.endTime).format('DD/MM HH:mm') : '--';
  return `${lineup} -> ${end}`;
});
const filterRangeMs = computed(() => {
  const { startDate, endDate } = storedRange;
  return {
    from: startDate ? moment.utc(startDate).startOf('day').valueOf() : null,
    to: endDate ? moment.utc(endDate).endOf('day').valueOf() : null,
  };
});
const processingActionLabel = computed(() => {
  if (processingAction.value === 'report') return 'loading report';
  if (processingAction.value === 'generate') return 'generating';
  if (processingAction.value === 'analyze') return 'analyzing';
  return 'loading';
});

// On direct navigation / refresh the events list may be empty. Deep-links open
// a report by event id, so fetch that one event directly (ignoring the persisted
// filter range) — otherwise an event outside the range wouldn't be found and the
// report would render empty.
async function ensureEventsLoaded() {
  if (event.value) return;
  await store.dispatch('eventAnalytics/fetchEventAnalyticsEventById', { eventId: eventId.value });
}
// Pull the AWS runtime info for this event's resources. The Grafana MediaConnect
// dashboards are keyed by the *MediaConnect* flow name, which differs from the
// name stored in our DB, so the details panel reads it from these infos.
function ensureResourceInfos() {
  const ev = event.value;
  if (!ev) return;
  const inputIds = [ev.input, ev.inputOverride].filter(Boolean).map((i) => i.id).filter(Boolean);
  const outputIds = ((ev.outputs && ev.outputs.items) || [])
    .map((item) => item && item.output)
    .filter((output) => output && ['MEDIA_CONNECT', 'MEDIA_LIVE'].includes(output.outputType))
    .map((output) => output.id);
  if (inputIds.length) store.dispatch('sourceMonitoring/fetchInputInfos', inputIds);
  if (outputIds.length) store.dispatch('sourceMonitoring/fetchOutputInfos', outputIds);
}
async function ensureReport() {
  if (!eventId.value) return;
  const report = getEventReport(eventId.value);
  const wantId = reportId.value;
  const haveId = report && (report.reportId || report.id);
  // Fetch when there's no data yet, or when the URL pins a different report.
  if (!report || !report.data || (wantId && wantId !== haveId)) {
    reportLoadingActive.value = true;
    try {
      await store.dispatch('eventAnalytics/fetchEventAnalyticsReport', {
        eventId: eventId.value,
        reportId: wantId || (report && report.reportId),
      });
    } finally {
      reportLoadingActive.value = false;
    }
  }
  if (event.value) store.dispatch('eventAnalytics/normalizeEventAnalytics', { event: event.value });
}
// A deep-link to an info-only incident must not land on an empty drilldown just
// because info anomalies are currently filtered out.
function revealIfInfo(group) {
  if (group && group.severity === 'info') showInfo.value = true;
}
function syncIncidentFromRoute() {
  const incidentId = proxy.$route.query.incidentId;
  if (!incidentId) return;
  const group = incidentGroups.value.find((item) => item.id === incidentId);
  if (group) {
    revealIfInfo(group);
    selectedIncidentId.value = group.id;
    selectedGroupStartMs.value = group.startMs;
    const anomalyId = proxy.$route.query.anomalyId;
    selectedAnomalyId.value = anomalyId
      && (group.anomalies || []).some((a) => a.id === anomalyId)
      ? anomalyId
      : null;
  }
}
// Single writer for all analytics query params. Managed keys are always rebuilt
// from refs (not from the possibly-stale $route.query), so concurrent calls
// during load can't drop each other's values.
function syncQueryToRoute() {
  const query = { ...proxy.$route.query };
  const set = (key, value) => { if (value) query[key] = value; else delete query[key]; };
  set('reportId', reportId.value);
  set('incidentId', selectedIncidentId.value);
  set('anomalyId', selectedAnomalyId.value);
  // resource* are inbound hints; once resolved to an incident, drop them.
  set('resourceId', selectedIncidentId.value ? null : selectedResourceId.value);
  set('resourceName', selectedIncidentId.value ? null : selectedResourceName.value);
  const keys = ['reportId', 'incidentId', 'anomalyId', 'resourceId', 'resourceName'];
  if (keys.every((k) => query[k] === proxy.$route.query[k])) return;
  proxy.$router.replace({ query }).catch(() => {});
}
// Resolve an inbound ?resourceId=/?resourceName= hint to the incident that
// affects that resource and open straight to it.
function syncResourceFromRoute() {
  if (selectedIncidentId.value) return;
  const rid = selectedResourceId.value;
  const rname = (selectedResourceName.value || '').toLowerCase();
  if (!rid && !rname) return;
  const matches = (a) => (rid && a.resourceId === rid)
    || (rname && `${a.resourceName || ''}`.toLowerCase() === rname);
  const group = incidentGroups.value.find((g) => (g.anomalies || []).some(matches));
  if (!group) return;
  const anomaly = (group.anomalies || []).find(matches);
  revealIfInfo(group);
  selectedIncidentId.value = group.id;
  selectedGroupStartMs.value = group.startMs;
  selectedAnomalyId.value = (anomaly && anomaly.id) || null;
  syncQueryToRoute();
}
function toggleShowInfo() {
  showInfo.value = !showInfo.value;
  try {
    window.localStorage.setItem(SHOW_INFO_STORAGE_KEY, showInfo.value ? '1' : '0');
  } catch (error) {
    // ignore storage failures
  }
  // The open incident may have just been filtered out.
  if (!selectedGroup.value) clearSelectedGroup();
}
function goBack() {
  proxy.$router.push({ name: 'eventanalytics', query: { eventId: eventId.value } }).catch(() => {});
}
function eventStatusTagClass(status) {
  const upper = `${status || ''}`.toUpperCase();
  if (upper.includes('FAIL') || upper.includes('ERROR') || upper.includes('DOWN')) return 'bg-red-500/20 text-red-200';
  if (upper.includes('LIVE') || upper.includes('COMPLETE')) return 'bg-emerald-500/20 text-emerald-200';
  if (upper.includes('PENDING') || upper.includes('CONFIRMED') || upper.includes('LINEUP') || upper.includes('RECEIVED')) return 'bg-amber-500/20 text-amber-200';
  return 'bg-white/15 text-white/75';
}
// Accepts either a group object or `{ group, anomalyId }` from the resource
// timeline (anomalyId set ⇒ open straight to that specific issue's details).
function handleIncidentSelect(payload) {
  const group = (payload && payload.group) || payload;
  if (!group) return;
  selectedIncidentId.value = group.id;
  selectedGroupStartMs.value = group.startMs;
  selectedAnomalyId.value = (payload && payload.anomalyId) || null;
  syncQueryToRoute();
}
function clearSelectedGroup() {
  selectedIncidentId.value = null;
  selectedGroupStartMs.value = null;
  selectedAnomalyId.value = null;
  syncQueryToRoute();
}
// Re-point the open incident at its refreshed group after re-normalizing so
// freshly added AI analysis is reflected.
function refreshSelectedGroup() {
  if (selectedGroupStartMs.value === null && !selectedIncidentId.value) return;
  const match = selectedGroup.value;
  if (match) {
    selectedIncidentId.value = match.id;
    selectedGroupStartMs.value = match.startMs;
  }
}
async function refreshReport() {
  if (!event.value) return;
  processingAction.value = 'report';
  try {
    const result = await store.dispatch('eventAnalytics/fetchEventAnalyticsReport', { eventId: eventId.value });
    if (result && result.error) {
      proxy.$message.error('Can not fetch analytics report.');
      return;
    }
    store.dispatch('eventAnalytics/normalizeEventAnalytics', { event: event.value });
    refreshSelectedGroup();
    proxy.$message.success('Analytics report loaded');
  } finally {
    processingAction.value = null;
  }
}
async function generateReport() {
  if (!event.value) return;
  processingAction.value = 'generate';
  try {
    const result = await store.dispatch('eventAnalytics/generateEventAnalyticsReport', {
      eventId: eventId.value,
      config: {
        aiSummary: aiConfig.aiSummary,
        incidentsAiAnalysis: aiConfig.incidentsAiAnalysis,
        aiProvider: aiConfig.aiProvider,
        aiModel: aiConfig.aiModel,
      },
    });
    if ((result && result.error) || !result || !result.success) {
      proxy.$message.error((result && result.message) || 'Report generation failed.');
      return;
    }
    proxy.$message.success(result.message || 'Report generated');
    await refreshReport();
  } finally {
    processingAction.value = null;
  }
}
async function runAnalysis(incidentIds) {
  if (!event.value) return;
  const report = getEventReport(eventId.value);
  // Local — distinct from the module-level `reportId` ref that mirrors the URL.
  const analyzeReportId = report && (report.reportId || report.id);
  if (!analyzeReportId) {
    proxy.$message.error('No report to analyze. Generate the report first.');
    return;
  }
  processingAction.value = 'analyze';
  try {
    const result = await store.dispatch('eventAnalytics/analyzeEventAnalyticsIncidents', {
      reportId: analyzeReportId,
      incidentIds,
      config: {
        aiProvider: aiConfig.aiProvider,
        aiModel: aiConfig.aiModel,
        aiMaxTokens: aiConfig.aiMaxTokens,
        generateSummary: true,
      },
    });
    if ((result && result.error) || !result || !result.success) {
      proxy.$message.error((result && result.message) || 'Incident analysis failed.');
      return;
    }
    proxy.$message.success(result.message || 'Incident analysis complete');
    await store.dispatch('eventAnalytics/fetchEventAnalyticsReport', { eventId: eventId.value });
    store.dispatch('eventAnalytics/normalizeEventAnalytics', { event: event.value });
    refreshSelectedGroup();
  } finally {
    processingAction.value = null;
  }
}
async function analyzeAll() {
  await runAnalysis('all');
}
async function handleAnalyzeIncidents({ incidentIds }) {
  await runAnalysis(incidentIds);
}

// Reload everything when navigating between detail pages (Vue reuses the view).
async function onRouteEventChange(id) {
  eventId.value = id;
  reportId.value = proxy.$route.query.reportId || null;
  selectedIncidentId.value = proxy.$route.query.incidentId || null;
  selectedAnomalyId.value = proxy.$route.query.anomalyId || null;
  selectedResourceId.value = proxy.$route.query.resourceId || null;
  selectedResourceName.value = proxy.$route.query.resourceName || null;
  selectedGroupStartMs.value = null;
  await ensureEventsLoaded();
  ensureResourceInfos();
  await ensureReport();
  syncIncidentFromRoute();
  syncResourceFromRoute();
}

watch(() => proxy.$route.params.eventId, onRouteEventChange);

// Keep reportId / the URL in sync with whichever report is actually loaded for
// this event — covers refresh, generate and analyze without extra plumbing.
watch(
  () => {
    const r = getEventReport(eventId.value);
    return r ? (r.reportId || r.id) : null;
  },
  (id) => {
    if (!id || id === reportId.value) return;
    reportId.value = id;
    syncQueryToRoute();
  },
);

onMounted(async () => {
  await ensureEventsLoaded();
  ensureResourceInfos();
  await ensureReport();
  syncIncidentFromRoute();
  syncResourceFromRoute();
});
</script>
